import type { LoginResponse } from '@bco/types';
import {
  authApi,
  BascAlert,
  BascBox,
  BascButton,
  BascCard,
  BascCenter,
  BascFormErrorSummary,
  BascGroup,
  BascIcon,
  BascLanguageSelector,
  BascPinInput,
  BascQrCode,
  BascSaveCodesGate,
  BascSkeleton,
  BascStack,
  BascStepper,
  BascText,
  BascTitle,
  CommonIcons,
  MfaError,
  notifications,
  useAuthStore,
  useTranslation,
} from '@bco/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BrandLogo } from '../../components/BrandLogo';
import { loadClientAssets } from '../../federationInit';
import { buildLoginBackgroundStyle } from '../../utils/loginBackgroundStyle';
import './setup-mfa.css';

interface ClientAssets {
  appLogo?: string;
  appLogoDark?: string;
  loginBackground?: string;
}

/** Router-state payload handed over from the Login MFA_ENROLLMENT_REQUIRED arm. */
interface SetupMfaLocationState {
  enrollmentToken?: string;
  challengeExpiresAt?: number;
}

type Phase = 'scan' | 'verify' | 'save';

/**
 * Mandatory two-step verification enrollment interstitial.
 *
 * This page REPLACES the shell (it is NOT wrapped in RequireAuth). The
 * enrollment token arrives ONLY via in-memory router `location.state`; it is
 * never persisted. Refreshing or deep-linking loses that state — by design we
 * then bounce back to the login screen and the user restarts.
 */
export function SetupMfaPage() {
  const { clientName = '' } = useParams<{ clientName: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // RequireEnrollment self-guard: snapshot the token from router state on mount.
  const state = location.state as SetupMfaLocationState | null;
  const [enrollmentToken] = useState<string | null>(
    state?.enrollmentToken ?? null
  );
  const [enrollmentExpiresAt] = useState<number | null>(
    state?.challengeExpiresAt ?? null
  );

  const [assets, setAssets] = useState<ClientAssets | null>(null);
  const [phase, setPhase] = useState<Phase>('scan');

  // Begin (QR) state
  const [otpauthUri, setOtpauthUri] = useState('');
  const [secretCode, setSecretCode] = useState('');
  const [beginLoading, setBeginLoading] = useState(true);
  const [beginError, setBeginError] = useState(false);

  // Verify state
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  // Save / complete state
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState(false);

  // Enrollment window expired (token lapsed)
  const [expired, setExpired] = useState(false);

  const pinRef = useRef<HTMLInputElement>(null);
  const verifyErrorId = 'setup-mfa-verify-error';

  // Self-guard: no token (refresh/deep-link) → back to login.
  useEffect(() => {
    if (!enrollmentToken) {
      navigate(`/${clientName}/login`, { replace: true });
    }
  }, [enrollmentToken, clientName, navigate]);

  useEffect(() => {
    async function loadAssets() {
      const clientAssets = await loadClientAssets(clientName);
      setAssets(clientAssets);
    }
    if (clientName) {
      loadAssets();
    }
  }, [clientName]);

  // Trip the expired path once the enrollment window passes.
  useEffect(() => {
    if (enrollmentExpiresAt == null) {
      return;
    }
    const check = () => {
      if (Date.now() / 1000 >= enrollmentExpiresAt) {
        setExpired(true);
      }
    };
    check();
    const id = window.setInterval(check, 1000);
    return () => window.clearInterval(id);
  }, [enrollmentExpiresAt]);

  const runBegin = useCallback(async () => {
    if (!enrollmentToken) {
      return;
    }
    setBeginLoading(true);
    setBeginError(false);
    try {
      const res = await authApi.mfaEnrollBegin(enrollmentToken);
      setOtpauthUri(res.otpauthUri);
      setSecretCode(res.secretCode);
    } catch (err) {
      if (err instanceof MfaError && err.code === 'MFA_SESSION_EXPIRED') {
        setExpired(true);
      } else {
        // Network / unexpected: offer a retry without leaving the page.
        setBeginError(true);
      }
    } finally {
      setBeginLoading(false);
    }
  }, [enrollmentToken]);

  useEffect(() => {
    if (enrollmentToken) {
      void runBegin();
    }
  }, [enrollmentToken, runBegin]);

  const submitVerify = useCallback(
    async (value: string) => {
      if (!enrollmentToken || verifying) {
        return;
      }
      setVerifying(true);
      setVerifyError(null);
      try {
        const res = await authApi.mfaEnrollVerify(enrollmentToken, value);
        setBackupCodes(res.backupCodes);
        setPhase('save');
      } catch (err) {
        if (err instanceof MfaError && err.code === 'MFA_SESSION_EXPIRED') {
          setExpired(true);
        } else if (err instanceof MfaError && err.code === 'MFA_CODE_INVALID') {
          // Stay on verify; do NOT re-begin. Clear + refocus.
          setVerifyError(t('auth.mfa.error.codeInvalid'));
          setCode('');
          window.requestAnimationFrame(() => pinRef.current?.focus());
        } else {
          setVerifyError(t('errors.network'));
        }
      } finally {
        setVerifying(false);
      }
    },
    [enrollmentToken, verifying, t]
  );

  const handleComplete = useCallback(async () => {
    if (!enrollmentToken || completing) {
      return;
    }
    setCompleting(true);
    setCompleteError(false);
    try {
      const response: LoginResponse =
        await authApi.mfaEnrollComplete(enrollmentToken);
      // Authenticate the store + mark enrolled so host + remotes are consistent.
      useAuthStore.setState({ accessToken: response.accessToken });
      useAuthStore.getState().setUser(response.user);
      useAuthStore.getState().setMfaEnrolled(true);
      notifications.show({
        message: t('auth.mfa.setup.complete'),
        color: 'green',
      });
      navigate(`/${clientName}`, { replace: true });
    } catch (err) {
      if (err instanceof MfaError && err.code === 'MFA_SESSION_EXPIRED') {
        // Codes are already shown; the session lapsed at the final step.
        setExpired(true);
      } else {
        // Keep the codes visible; allow retry of complete only.
        setCompleteError(true);
      }
    } finally {
      setCompleting(false);
    }
  }, [enrollmentToken, completing, clientName, navigate, t]);

  const handleLogout = useCallback(() => {
    // Clear any local token and return to login. The held Cognito token never
    // reached the SPA; only the enrollment token (in-memory) is dropped here.
    useAuthStore.getState().clearAuthStorage();
    navigate(`/${clientName}/login`, { replace: true });
  }, [clientName, navigate]);

  const startOver = useCallback(() => {
    navigate(`/${clientName}/login`, { replace: true });
  }, [clientName, navigate]);

  // While the guard redirect is pending, render nothing.
  if (!enrollmentToken) {
    return null;
  }

  const activeStep = phase === 'scan' ? 0 : phase === 'verify' ? 1 : 2;

  const backgroundStyle = buildLoginBackgroundStyle(assets);

  return (
    <BascBox className="setup-mfa-page" style={backgroundStyle}>
      <BascBox className="setup-mfa-overlay" />

      {/* Top row replaces the shell header: logout + language. */}
      <BascGroup
        className="setup-mfa-topbar"
        justify="space-between"
        wrap="nowrap"
        px="md"
        py="sm"
      >
        {assets?.appLogo ? (
          <BrandLogo
            src={assets.appLogo}
            srcDark={assets.appLogoDark}
            alt="BCO PORTAL"
            className="setup-mfa-logo"
          />
        ) : (
          <BascBox />
        )}
        <BascGroup gap="sm" wrap="nowrap">
          <BascLanguageSelector compact label={t('language.change')} />
          <BascButton variant="subtle" size="xs" onClick={handleLogout}>
            {t('auth.logout')}
          </BascButton>
        </BascGroup>
      </BascGroup>

      <BascCenter className="setup-mfa-container">
        <BascCard
          className="setup-mfa-card"
          shadow="xl"
          padding="xl"
          radius="md"
        >
          <BascStack gap="lg">
            <BascStack gap={4} align="center">
              <BascIcon name={CommonIcons.SHIELD} size={32} />
              <BascTitle order={1} textRole="pageTitle" size="h3" ta="center">
                {t('auth.mfa.setup.title')}
              </BascTitle>
              <BascText ta="center" textRole="body" c="dimmed">
                {t('auth.mfa.setup.intro')}
              </BascText>
            </BascStack>

            <BascStepper active={activeStep} size="sm">
              <BascStepper.Step label={t('auth.mfa.setup.stepScan')} />
              <BascStepper.Step label={t('auth.mfa.setup.stepVerify')} />
              <BascStepper.Step label={t('auth.mfa.setup.stepSave')} />
            </BascStepper>

            {expired ? (
              <BascStack gap="md">
                <BascAlert color="yellow" variant="light">
                  {t('auth.mfa.error.enrollmentExpired')}
                </BascAlert>
                <BascButton emphasis="primary" fullWidth onClick={startOver}>
                  {t('auth.mfa.setup.startOver')}
                </BascButton>
              </BascStack>
            ) : phase === 'scan' ? (
              <BascStack gap="md">
                {beginError ? (
                  <BascStack gap="md">
                    <BascAlert color="red" variant="light">
                      {t('auth.mfa.error.qrFailed')}
                    </BascAlert>
                    <BascButton
                      emphasis="primary"
                      fullWidth
                      onClick={() => void runBegin()}
                      leftSection={
                        <BascIcon name={CommonIcons.REFRESH} size={16} />
                      }
                    >
                      {t('auth.mfa.retry')}
                    </BascButton>
                  </BascStack>
                ) : (
                  <>
                    <BascText textRole="body">
                      {t('auth.mfa.setup.installApp')}
                    </BascText>
                    <BascText textRole="body">
                      {t('auth.mfa.setup.scanInstruction')}
                    </BascText>
                    {beginLoading ? (
                      <BascCenter>
                        <BascSkeleton width={200} height={200} radius="sm" />
                      </BascCenter>
                    ) : (
                      <BascQrCode
                        otpauthUri={otpauthUri}
                        secretCode={secretCode}
                        label={t('auth.mfa.setup.title')}
                      />
                    )}
                    <BascButton
                      emphasis="primary"
                      fullWidth
                      disabled={beginLoading || !otpauthUri}
                      onClick={() => setPhase('verify')}
                    >
                      {t('auth.mfa.setup.continue')}
                    </BascButton>
                  </>
                )}
              </BascStack>
            ) : phase === 'verify' ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (code.length === 6) {
                    void submitVerify(code);
                  }
                }}
              >
                <BascStack gap="md">
                  <BascText textRole="body" ta="center">
                    {t('auth.mfa.setup.verifyInstruction')}
                  </BascText>

                  {/* Wrapper keeps the stable id the PIN points at via
                      aria-describedby. autoFocus is off so an invalid code keeps
                      focus on the PIN (re-entry), while the summary still
                      announces as an alert. */}
                  <div id={verifyErrorId}>
                    <BascFormErrorSummary
                      autoFocus={false}
                      errors={verifyError ? [{ message: verifyError }] : []}
                    />
                  </div>

                  <BascGroup justify="center">
                    <BascPinInput
                      ref={pinRef}
                      value={code}
                      onChange={setCode}
                      onComplete={(value) => void submitVerify(value)}
                      autoFocus
                      disabled={verifying}
                      error={!!verifyError}
                      aria-label={t('auth.mfa.codeLabel')}
                      aria-describedby={verifyErrorId}
                      name="totp-enroll-code"
                    />
                  </BascGroup>

                  <BascButton
                    emphasis="primary"
                    type="submit"
                    fullWidth
                    loading={verifying}
                    disabled={code.length < 6}
                  >
                    {t('auth.mfa.verify')}
                  </BascButton>

                  <BascButton
                    type="button"
                    variant="subtle"
                    fullWidth
                    onClick={() => {
                      setPhase('scan');
                      setVerifyError(null);
                      setCode('');
                    }}
                  >
                    {t('back')}
                  </BascButton>
                </BascStack>
              </form>
            ) : (
              <BascSaveCodesGate
                codes={backupCodes}
                clientName={clientName}
                onDone={() => void handleComplete()}
                doneLoading={completing}
              >
                {completeError && (
                  <BascAlert color="red" variant="light" role="alert">
                    {t('auth.mfa.error.completeFailed')}
                  </BascAlert>
                )}
              </BascSaveCodesGate>
            )}
          </BascStack>
        </BascCard>
      </BascCenter>
    </BascBox>
  );
}

export default SetupMfaPage;
